import React, { forwardRef } from 'react'
import styled from 'styled-components'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

import { ChatMessage } from 'hooks/useTechAssistantChat'
import CopyButton from 'components/Chatbot/CopyButton'

type Props = {
  messages: ChatMessage[]
  isSourceExpanded?: boolean
}

const ChatMessageList = forwardRef<HTMLDivElement, Props>(({ messages, isSourceExpanded = false }, ref) => {
  return (
    <S.Container>
      <S.List $isSourceExpanded={isSourceExpanded}>
        {messages.map((msg) => {
          const isUser = msg.sender === 'You'
          if (msg.content === '' && !msg.searching) {
            return null
          }
          return (
            <S.MessageRow key={msg.id} $isUser={isUser}>
              <S.Bubble $isUser={isUser}>
                {msg.searching
                  ? <S.Searching><S.BouncingDots><span /><span /><span /></S.BouncingDots></S.Searching>
                  : msg.content}
                {!isUser && !msg.searching && <CopyButton content={msg.content} />}
              </S.Bubble>
            </S.MessageRow>
          )
        })}
        <div ref={ref} />
      </S.List>
    </S.Container>
  )
})

ChatMessageList.displayName = 'ChatMessageList'

export default ChatMessageList

const S = {
  Container: styled.div`
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `,
  List: styled.div<{ $isSourceExpanded: boolean }>`
    flex: 1;
    max-height: ${({ $isSourceExpanded }) => ($isSourceExpanded ? '50vh' : '70vh')};
    min-height: 0;
    overflow-y: auto;
    transition: max-height 0.3s ease;

    @media ${screenSize.small} {
      max-height: ${({ $isSourceExpanded }) => ($isSourceExpanded ? '50vh' : '60vh')};
    }
  `,
  MessageRow: styled.div<{ $isUser: boolean }>`
    display: flex;
    margin-top: 20px;
    justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  `,
  Bubble: styled.div<{ $isUser: boolean }>`
    position: relative;
    margin-top: 20px;
    max-width: 80%;
    padding: 0.5rem 0.75rem;
    padding-right: 1.75rem;
    color: ${({ $isUser }) => ($isUser ? color('chatbotMessage') : color('chatbotReply'))};
    background-color: ${({ $isUser }) => ($isUser ? color('chatbotMessageBackground') : color('chatbotReplyBackground'))};
    border-radius: 12px;
    font-size: 0.875rem;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;

    &:hover button {
      opacity: 1;
    }

    @media ${screenSize.small} {
      max-width: 90%;
      padding: 0.5rem 0.625rem;
      padding-right: 1.75rem;
      font-size: 0.8125rem;
    }
  `,
  Searching: styled.span`
    display: inline-flex;
    align-items: center;
    min-height: 1.4em;
  `,
  BouncingDots: styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    @keyframes bounce {
      0%, 80%, 100% { transform: translateY(0); }
      40% { transform: translateY(-6px); }
    }
    span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: currentColor;
      opacity: 0.6;
      animation: bounce 1.4s infinite ease-in-out;
      &:nth-child(2) { animation-delay: 0.2s; }
      &:nth-child(3) { animation-delay: 0.4s; }
    }
  `
}
